import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { type UseMutationResult, useMutation } from '@tanstack/react-query';
import { StorageKey } from '../../hooks/model/StorageKey';
import { useLocalStoragePubSub } from '../../hooks/useLocalStoragePubSub';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { setPreviewDateInUrl } from '../../utils/preview-mode-utils';
import { bffDelete } from '../bff';
import { type CustomerContext } from './model/CustomerContext';

export const useCustomerClearContext = (): UseMutationResult<
    CustomerContext,
    unknown,
    undefined
> => {
    const pwaSurlatablePreview = useCustomPreferenceOrDefault(
        CustomPreference.PWA_SURLATABLE_PREVIEW,
        false,
    );
    const publishPreviewDate = useLocalStoragePubSub(StorageKey.PREVIEW_MODE_DATE);
    const authContext = useAuthContext();
    return useMutation<CustomerContext, unknown, undefined>({
        mutationFn: async () => {
            if (!pwaSurlatablePreview) {
                throw new Error('PWA Surlatable Preview is not enabled');
            }
            const authData = await authContext.ready();
            if (!authData.usid) {
                throw new Error('User ID is required');
            }
            return bffDelete<CustomerContext>(`/contexts/${authData.usid}`, {
                httpParams: [],
                accessToken: authData.access_token,
                customerId: authData.customer_id,
            });
        },
        onSuccess: () => {
            try {
                publishPreviewDate('');
            } catch (error) {
                // Log but don't fail the mutation
                console.warn('Failed to publish preview date:', error);
            }
            setPreviewDateInUrl(null);
        },
    });
};
